import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { routeService } from '../../../services/routeService';
import './AdminRoutes.css';

const AdminRouteEdit = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    mainPhotoId: null,
    additionalInfo: [],
    points: [],
    photoIds: [],
    distance: 0,
    time: 0,
    steps: 0
  });
  
  const [existingPhotos, setExistingPhotos] = useState([]);
  const [existingMainPhoto, setExistingMainPhoto] = useState(null);
  const [pointPhotos, setPointPhotos] = useState({});
  
  const [tempMainPhoto, setTempMainPhoto] = useState(null);
  const [mainPhotoPreview, setMainPhotoPreview] = useState(null);
  const [tempPhotos, setTempPhotos] = useState([]);
  const [tempPointPhotos, setTempPointPhotos] = useState({});
  
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [calculating, setCalculating] = useState(false);
  const [coordinateErrors, setCoordinateErrors] = useState({});

  useEffect(() => {
    fetchRouteData();
  }, [id]);

  // Парсинг строки координат
  const parseCoordinates = (coordString) => {
    if (!coordString || typeof coordString !== 'string') return null;
    
    const parts = coordString.trim().split(',').map(part => part.trim());
    
    if (parts.length !== 2) return null;
    
    const lat = parseFloat(parts[0]);
    const lng = parseFloat(parts[1]);
    
    if (isNaN(lat) || isNaN(lng)) return null;
    if (lat < -90 || lat > 90) return null;
    if (lng < -180 || lng > 180) return null;
    
    return { latitude: lat, longitude: lng };
  };

  // Расчет данных маршрута по точкам
  const calculateRouteFromPoints = (points) => {
    if (points.length < 2) {
      setFormData(prev => ({
        ...prev,
        distance: 0,
        time: 0,
        steps: 0
      }));
      return;
    }

    setCalculating(true);

    let totalDistance = 0;
    for (let i = 0; i < points.length - 1; i++) {
      const p1 = points[i];
      const p2 = points[i + 1];
      
      const R = 6371;
      const dLat = (p2.latitude - p1.latitude) * Math.PI / 180;
      const dLon = (p2.longitude - p1.longitude) * Math.PI / 180;
      const a = 
        Math.sin(dLat/2) * Math.sin(dLat/2) +
        Math.cos(p1.latitude * Math.PI/180) * Math.cos(p2.latitude * Math.PI/180) * 
        Math.sin(dLon/2) * Math.sin(dLon/2);
      const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
      const distance = R * c;
      totalDistance += distance;
    }

    const timeHours = totalDistance / 5;
    const timeMinutes = Math.round(timeHours * 60);
    const calculatedSteps = Math.round((totalDistance * 1000) / 0.75);

    setFormData(prev => ({
      ...prev,
      distance: parseFloat(totalDistance.toFixed(2)),
      time: timeMinutes,
      steps: calculatedSteps
    }));
    
    setCalculating(false);
  };

  const fetchRouteData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const routeData = await routeService.getRouteById(id);
      console.log('Загруженные данные маршрута:', routeData);
      
      const mainPhotoIdValue = routeData.mainPhotoId || routeData.main_photo_id;
      const photoIdsValue = routeData.photoIds || routeData.photo_ids || [];
      
      const additionalInfoList = (routeData.additionalInfo || []).map((info) => ({
        id: info.id,
        title: info.title || '',
        description: info.description || ''
      }));
      
      // Преобразуем точки: из отдельных полей latitude/longitude в одно поле coordinates
      const pointsList = (routeData.points || []).map((point) => ({
        id: point.id,
        name: point.name || '',
        description: point.description || '',
        coordinates: `${point.latitude}, ${point.longitude}`,
        latitude: point.latitude,
        longitude: point.longitude,
        photos: point.photos || []
      }));
      
      setFormData({
        name: routeData.name || '',
        description: routeData.description || '',
        mainPhotoId: mainPhotoIdValue,
        additionalInfo: additionalInfoList,
        points: pointsList,
        photoIds: photoIdsValue,
        distance: routeData.distance || 0,
        time: routeData.time || 0,
        steps: routeData.steps || 0
      });
      
      // Общие фотографии (исключая основную)
      const allPhotos = routeData.photos || [];
      if (mainPhotoIdValue) {
        const mainPhotoFromList = allPhotos.find(p => p.id === mainPhotoIdValue);
        if (mainPhotoFromList) {
          setExistingMainPhoto(mainPhotoFromList);
          setExistingPhotos(allPhotos.filter(p => p.id !== mainPhotoIdValue));
        } else {
          setExistingPhotos(allPhotos);
        }
      } else {
        setExistingPhotos(allPhotos);
      }
      
      if (mainPhotoIdValue && !existingMainPhoto) {
        try {
          const mainPhoto = await routeService.getMainPhoto(id);
          if (mainPhoto && mainPhoto.id) {
            setExistingMainPhoto(mainPhoto);
          }
        } catch (err) {
          console.error('Ошибка загрузки основной фотографии:', err);
        }
      }
      
      // Загружаем фотографии для каждой точки
      const pointPhotosMap = {};
      for (let i = 0; i < pointsList.length; i++) {
        const point = pointsList[i];
        if (point.id) {
          if (point.photos && point.photos.length > 0) {
            pointPhotosMap[point.id] = point.photos;
          } else {
            try {
              const pointPhotosResponse = await routeService.getPointPhotos(point.id);
              if (pointPhotosResponse && pointPhotosResponse.length > 0) {
                pointPhotosMap[point.id] = pointPhotosResponse;
                const updatedPoints = [...pointsList];
                updatedPoints[i].photos = pointPhotosResponse;
                setFormData(prev => ({ ...prev, points: updatedPoints }));
              }
            } catch (err) {
              console.error(`Ошибка загрузки фото для точки ${point.id}:`, err);
              pointPhotosMap[point.id] = [];
            }
          }
        }
      }
      setPointPhotos(pointPhotosMap);
      
      setLoading(false);
    } catch (error) {
      console.error('Ошибка загрузки данных:', error);
      setError('Не удалось загрузить данные маршрута');
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  // ========== ОСНОВНАЯ ФОТОГРАФИЯ ==========

  const handleMainPhotoSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      setTempMainPhoto(file);
      const previewUrl = URL.createObjectURL(file);
      setMainPhotoPreview(previewUrl);
    }
  };

  const removeMainPhoto = async () => {
    if (window.confirm('Удалить основную фотографию?')) {
      if (existingMainPhoto) {
        try {
          await routeService.deleteRoutePhoto(id, existingMainPhoto.id);
          setExistingMainPhoto(null);
          setFormData(prev => ({ ...prev, mainPhotoId: null }));
        } catch (err) {
          console.error('Ошибка удаления основной фотографии:', err);
          setError('Не удалось удалить основную фотографию');
        }
      }
      
      if (mainPhotoPreview) {
        URL.revokeObjectURL(mainPhotoPreview);
        setTempMainPhoto(null);
        setMainPhotoPreview(null);
      }
    }
  };

  // ========== ДОПОЛНИТЕЛЬНАЯ ИНФОРМАЦИЯ ==========

  const handleAdditionalInfoChange = (index, field, value) => {
    const updatedInfo = [...formData.additionalInfo];
    updatedInfo[index][field] = value;
    setFormData(prev => ({ ...prev, additionalInfo: updatedInfo }));
  };

  const addAdditionalInfo = () => {
    setFormData(prev => ({
      ...prev,
      additionalInfo: [...prev.additionalInfo, { title: '', description: '' }]
    }));
  };

  const removeAdditionalInfo = (index) => {
    const updatedInfo = formData.additionalInfo.filter((_, i) => i !== index);
    setFormData(prev => ({ ...prev, additionalInfo: updatedInfo }));
  };

  // ========== ТОЧКИ (новая версия без карты) ==========

  const updatePointInfo = (index, field, value) => {
    const updatedPoints = [...formData.points];
    updatedPoints[index][field] = value;
    
    setFormData(prev => {
      // Если меняются координаты, нужно пересчитать маршрут после парсинга
      if (field === 'coordinates') {
        setCoordinateErrors(prevErrors => ({
          ...prevErrors,
          [index]: null
        }));
        
        const parsed = parseCoordinates(value);
        if (parsed && updatedPoints[index]) {
          updatedPoints[index].latitude = parsed.latitude;
          updatedPoints[index].longitude = parsed.longitude;
        }
        
        setTimeout(() => {
          const pointsForCalc = updatedPoints.map(p => ({
            latitude: p.latitude,
            longitude: p.longitude
          }));
          calculateRouteFromPoints(pointsForCalc);
        }, 100);
      }
      
      return { ...prev, points: updatedPoints };
    });
  };

  const addPoint = () => {
    setFormData(prev => {
      const newPoints = [
        ...prev.points,
        {
          id: null,
          name: '',
          description: '',
          coordinates: '',
          photos: []
        }
      ];
      return { ...prev, points: newPoints };
    });
  };

  const removePoint = async (index) => {
    if (formData.points.length > 1) {
      if (window.confirm('Удалить эту точку?')) {
        const point = formData.points[index];
        
        if (point.id && point.photos && point.photos.length > 0) {
          for (const photo of point.photos) {
            await routeService.deleteRoutePhoto(id, photo.id);
          }
        }
        
        const updatedPoints = formData.points.filter((_, i) => i !== index);
        setFormData(prev => {
          setTimeout(() => {
            const pointsForCalc = updatedPoints.map(p => ({
              latitude: p.latitude,
              longitude: p.longitude
            }));
            calculateRouteFromPoints(pointsForCalc);
          }, 100);
          return { ...prev, points: updatedPoints };
        });
        
        if (point.id) {
          setPointPhotos(prev => {
            const newMap = { ...prev };
            delete newMap[point.id];
            return newMap;
          });
        }
        
        const newTempPointPhotos = { ...tempPointPhotos };
        delete newTempPointPhotos[index];
        setTempPointPhotos(newTempPointPhotos);
        
        setCoordinateErrors(prev => {
          const newState = { ...prev };
          delete newState[index];
          return newState;
        });
      }
    }
  };

  const reorderPoints = (fromIndex, toIndex) => {
    const newPoints = [...formData.points];
    const [movedPoint] = newPoints.splice(fromIndex, 1);
    newPoints.splice(toIndex, 0, movedPoint);
    
    setFormData(prev => {
      setTimeout(() => {
        const pointsForCalc = newPoints.map(p => ({
          latitude: p.latitude,
          longitude: p.longitude
        }));
        calculateRouteFromPoints(pointsForCalc);
      }, 100);
      return { ...prev, points: newPoints };
    });
  };

  const clearAllPoints = async () => {
    if (window.confirm('Очистить все точки?')) {
      for (const point of formData.points) {
        if (point.id && point.photos && point.photos.length > 0) {
          for (const photo of point.photos) {
            await routeService.deleteRoutePhoto(id, photo.id);
          }
        }
      }
      
      setFormData(prev => {
        const newPoints = [{
          id: null,
          name: '',
          description: '',
          coordinates: '',
          photos: []
        }];
        setTimeout(() => calculateRouteFromPoints([]), 100);
        return { ...prev, points: newPoints };
      });
      setPointPhotos({});
      setTempPointPhotos({});
      setCoordinateErrors({});
    }
  };

  // Валидация всех координат точек
  const validateCoordinates = () => {
    const errors = {};
    let isValid = true;
    
    formData.points.forEach((point, index) => {
      if (!point.coordinates || point.coordinates.trim() === '') {
        errors[index] = 'Введите координаты';
        isValid = false;
      } else {
        const parsed = parseCoordinates(point.coordinates);
        if (!parsed) {
          errors[index] = 'Некорректный формат. Используйте формат: 55.751244, 37.618423';
          isValid = false;
        } else {
          // Обновляем latitude и longitude в точке для корректной отправки
          const updatedPoints = [...formData.points];
          updatedPoints[index].latitude = parsed.latitude;
          updatedPoints[index].longitude = parsed.longitude;
          setFormData(prev => ({ ...prev, points: updatedPoints }));
        }
      }
    });
    
    setCoordinateErrors(errors);
    return isValid;
  };

  // ========== ОБЩИЕ ФОТОГРАФИИ ==========

  const handlePhotoSelect = (e) => {
    const files = Array.from(e.target.files);
    setTempPhotos(prev => [...prev, ...files]);
  };

  const removeTempPhoto = (index) => {
    setTempPhotos(prev => prev.filter((_, i) => i !== index));
  };

  const removeExistingPhoto = async (photoId) => {
    if (window.confirm('Удалить это фото?')) {
      try {
        await routeService.deleteRoutePhoto(id, photoId);
        setExistingPhotos(prev => prev.filter(p => p.id !== photoId));
        setFormData(prev => ({
          ...prev,
          photoIds: prev.photoIds.filter(pid => pid !== photoId)
        }));
      } catch (err) {
        console.error('Ошибка удаления фото:', err);
        setError('Не удалось удалить фото');
      }
    }
  };

  const setAsMainPhoto = async (photoId) => {
    try {
      const newMainPhoto = existingPhotos.find(p => p.id === photoId);
      if (!newMainPhoto) return;
      
      await routeService.patchRoute(id, { mainPhotoId: photoId });
      
      setFormData(prev => ({ ...prev, mainPhotoId: photoId }));
      
      if (existingMainPhoto) {
        setExistingPhotos(prev => [...prev, existingMainPhoto]);
      }
      
      setExistingMainPhoto(newMainPhoto);
      setExistingPhotos(prev => prev.filter(p => p.id !== photoId));
      
      alert('Основная фотография обновлена');
    } catch (err) {
      console.error('Ошибка установки основной фотографии:', err);
      setError('Не удалось установить основную фотографию');
    }
  };

  // ========== ФОТОГРАФИИ ТОЧЕК ==========

  const handlePointPhotoSelect = (pointIndex, files) => {
    const filesArray = Array.from(files);
    setTempPointPhotos(prev => ({
      ...prev,
      [pointIndex]: [...(prev[pointIndex] || []), ...filesArray]
    }));
  };

  const removePointTempPhoto = (pointIndex, photoIndex) => {
    setTempPointPhotos(prev => ({
      ...prev,
      [pointIndex]: prev[pointIndex].filter((_, i) => i !== photoIndex)
    }));
  };

  const removePointPhoto = async (pointIndex, photoId) => {
    if (window.confirm('Удалить это фото?')) {
      try {
        await routeService.deleteRoutePhoto(id, photoId);
        
        const updatedPoints = [...formData.points];
        const point = updatedPoints[pointIndex];
        if (point.photos) {
          point.photos = point.photos.filter(p => p.id !== photoId);
        }
        setFormData(prev => ({ ...prev, points: updatedPoints }));
        
        if (point.id) {
          setPointPhotos(prev => ({
            ...prev,
            [point.id]: prev[point.id].filter(p => p.id !== photoId)
          }));
        }
      } catch (err) {
        console.error('Ошибка удаления фото точки:', err);
        setError('Не удалось удалить фото');
      }
    }
  };

  // ========== ПОДГОТОВКА И ОТПРАВКА ДАННЫХ ==========

  const prepareRouteData = () => {
    const data = {
      name: formData.name.trim(),
      description: formData.description.trim() || null,
      additionalInfo: formData.additionalInfo.map(info => ({
        id: info.id,
        title: info.title,
        description: info.description
      })),
      distance: parseFloat(formData.distance) || 0,
      time: parseInt(formData.time) || 0,
      steps: parseInt(formData.steps) || 0
    };
    
    // Подготовка точек с парсингом координат
    data.points = formData.points.map(point => {
      const parsed = parseCoordinates(point.coordinates);
      return {
        id: point.id,
        name: point.name,
        description: point.description,
        latitude: parsed ? parsed.latitude : point.latitude,
        longitude: parsed ? parsed.longitude : point.longitude
      };
    });
    
    return data;
  };

  // Загрузка всех новых фотографий
  const uploadNewPhotos = async () => {
    // 1. Загружаем новую основную фотографию
    if (tempMainPhoto) {
      try {
        console.log('Загрузка основной фотографии...');
        const uploaded = await routeService.uploadMainPhoto(id, tempMainPhoto);
        console.log('Основная фотография загружена:', uploaded);
        
        if (uploaded && uploaded.id) {
          setExistingMainPhoto(uploaded);
          setFormData(prev => ({ ...prev, mainPhotoId: uploaded.id }));
          await routeService.patchRoute(id, { main_photo_id: uploaded.id });
          console.log('Обновлен mainPhotoId на сервере:', uploaded.id);
        }
        
        setTempMainPhoto(null);
        setMainPhotoPreview(null);
      } catch (error) {
        console.error('Ошибка загрузки основной фотографии:', error);
        throw new Error('Не удалось загрузить основную фотографию');
      }
    }
    
    // 2. Загружаем новые общие фотографии
    if (tempPhotos.length > 0) {
      try {
        console.log('Загрузка общих фотографий...');
        const uploaded = await routeService.uploadRoutePhotos(id, tempPhotos);
        const uploadedArray = Array.isArray(uploaded) ? uploaded : [uploaded];
        
        if (uploadedArray.length > 0) {
          setExistingPhotos(prev => [...prev, ...uploadedArray]);
          const currentRoute = await routeService.getRouteById(id);
          const currentPhotoIds = currentRoute.photo_ids || currentRoute.photoIds || [];
          const newPhotoIds = uploadedArray.map(p => p.id);
          const updatedPhotoIds = [...currentPhotoIds, ...newPhotoIds];
          await routeService.patchRoute(id, { photo_ids: updatedPhotoIds });
          console.log('Загружены новые общие фото:', uploadedArray.length);
        }
        
        setTempPhotos([]);
      } catch (error) {
        console.error('Ошибка загрузки общих фотографий:', error);
        throw new Error('Не удалось загрузить общие фотографии');
      }
    }

    // 3. Загружаем новые фотографии для точек
    for (let i = 0; i < formData.points.length; i++) {
      const pointFiles = tempPointPhotos[i] || [];
      if (pointFiles.length > 0) {
        const pointId = formData.points[i].id;
        if (pointId) {
          try {
            console.log(`Загрузка фото для точки ${i}...`);
            const uploaded = await routeService.uploadPointPhotos(pointId, pointFiles);
            const uploadedArray = Array.isArray(uploaded) ? uploaded : [uploaded];
            
            const updatedPoints = [...formData.points];
            updatedPoints[i].photos = [...(updatedPoints[i].photos || []), ...uploadedArray];
            setFormData(prev => ({ ...prev, points: updatedPoints }));
            
            setPointPhotos(prev => ({
              ...prev,
              [pointId]: [...(prev[pointId] || []), ...uploadedArray]
            }));
            
            console.log(`Загружены новые фото для точки ${i}:`, uploadedArray.length);
          } catch (error) {
            console.error(`Ошибка загрузки фото для точки ${i}:`, error);
            throw new Error(`Не удалось загрузить фото для точки ${i + 1}`);
          }
        }
      }
    }
    
    setTempPointPhotos({});
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name.trim()) {
      setError('Пожалуйста, введите название маршрута');
      return;
    }
    
    // Валидация координат всех точек
    if (!validateCoordinates()) {
      setError('Пожалуйста, исправьте ошибки в координатах точек');
      return;
    }
    
    setSubmitting(true);
    setError(null);

    try {
      console.log('Начало загрузки фотографий...');
      await uploadNewPhotos();
      console.log('Все фотографии успешно загружены');
      
      const routeData = prepareRouteData();
      console.log('Обновление данных маршрута:', routeData);
      await routeService.updateRoute(id, routeData);
      
      alert('Маршрут успешно обновлен!');
      navigate('/admin/locations/routes');
    } catch (err) {
      console.error('Ошибка при обновлении маршрута:', err);
      setError(err.message || 'Не удалось обновить маршрут');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="admin-loading">
        <div className="spinner"></div>
        <p>Загрузка данных маршрута...</p>
      </div>
    );
  }

  return (
    <div className="admin-route-form">
      <h2 className="admin-route-form_title">Редактирование маршрута</h2>

      {error && (
        <div className="admin-route-form_error">
          <span className="error-icon">⚠️</span> {error}
        </div>
      )}

      <form onSubmit={handleSubmit} className="admin-route-form_form">
        {/* ===== СЕКЦИЯ 1: ОСНОВНАЯ ИНФОРМАЦИЯ ===== */}
        <div className="admin-route-form_section">
          <h3 className="admin-route-form_section-title">Основная информация</h3>
          
          <div className="admin-route-form_field">
            <label htmlFor="name">
              Название <span className="required">*</span>
            </label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              required
              disabled={submitting}
              placeholder="Введите название маршрута"
              autoFocus
            />
          </div>

          <div className="admin-route-form_field">
            <label htmlFor="description">Описание</label>
            <textarea
              id="description"
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              rows="4"
              disabled={submitting}
              placeholder="Введите описание маршрута"
            />
          </div>
        </div>

        {/* ===== СЕКЦИЯ 2: ХАРАКТЕРИСТИКИ МАРШРУТА ===== */}
        <div className="admin-route-form_section">
          <h3 className="admin-route-form_section-title">Характеристики маршрута</h3>
          
          <div className="admin-route-form_route-data">
            <div className="admin-route-form_field">
              <label htmlFor="distance">Протяженность (км)</label>
              <input
                type="number"
                id="distance"
                name="distance"
                value={formData.distance}
                onChange={handleInputChange}
                step="0.1"
                disabled={calculating || submitting}
              />
            </div>

            <div className="admin-route-form_field">
              <label htmlFor="time">Время в пути (минут)</label>
              <input
                type="number"
                id="time"
                name="time"
                value={formData.time}
                onChange={handleInputChange}
                disabled={calculating || submitting}
              />
            </div>

            <div className="admin-route-form_field">
              <label htmlFor="steps">Количество шагов</label>
              <input
                type="number"
                id="steps"
                name="steps"
                value={formData.steps}
                onChange={handleInputChange}
                disabled={calculating || submitting}
              />
            </div>
          </div>

          {formData.points.length >= 2 && (
            <p className="admin-route-form_data-hint">
              * Характеристики автоматически пересчитываются при изменении точек маршрута
            </p>
          )}
        </div>

        {/* ===== СЕКЦИЯ 3: ОСНОВНАЯ ФОТОГРАФИЯ ===== */}
        <div className="admin-route-form_section">
          <h3 className="admin-route-form_section-title">Основная фотография</h3>
          
          <div className="admin-route-form_main-photo">
            {!tempMainPhoto && existingMainPhoto && (
              <div className="photo-item">
                <img 
                  src={existingMainPhoto.url} 
                  alt="Основная фотография"
                  onError={(e) => {
                    e.target.src = '/placeholder-image.jpg';
                  }}
                />
                <div className="photo-actions">
                  <label className={`btn btn-secondary btn-small ${submitting ? 'disabled' : ''}`}>
                    📁 Заменить
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleMainPhotoSelect}
                      style={{ display: 'none' }}
                      disabled={submitting}
                    />
                  </label>
                  <button 
                    type="button"
                    onClick={removeMainPhoto}
                    className="photo-remove"
                    disabled={submitting}
                    title="Удалить"
                  >
                    ✕
                  </button>
                </div>
              </div>
            )}

            {tempMainPhoto && mainPhotoPreview && (
              <div className="photo-item">
                <img 
                  src={mainPhotoPreview} 
                  alt="Новая основная фотография"
                />
                <div className="photo-actions">
                  <label className={`btn btn-secondary btn-small ${submitting ? 'disabled' : ''}`}>
                    📁 Заменить
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleMainPhotoSelect}
                      style={{ display: 'none' }}
                      disabled={submitting}
                    />
                  </label>
                  <button 
                    type="button"
                    onClick={removeMainPhoto}
                    className="photo-remove"
                    disabled={submitting}
                    title="Удалить"
                  >
                    ✕
                  </button>
                </div>
              </div>
            )}

            {!existingMainPhoto && !tempMainPhoto && (
              <div className="main-photo-upload">
                <label className={`btn btn-secondary ${submitting ? 'disabled' : ''}`}>
                  📁 Выберите основную фотографию
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleMainPhotoSelect}
                    style={{ display: 'none' }}
                    disabled={submitting}
                  />
                </label>
                <p className="hint">Рекомендуемый размер: 1200x800px</p>
              </div>
            )}
          </div>
        </div>

        {/* ===== СЕКЦИЯ 4: ОБЩИЕ ФОТОГРАФИИ ===== */}
        <div className="admin-route-form_section">
          <h3 className="admin-route-form_section-title">Общие фотографии</h3>
          
          {existingPhotos.length > 0 && (
            <div className="photo-grid">
              {existingPhotos.map(photo => (
                <div key={photo.id} className="photo-item">
                  <img 
                    src={photo.url} 
                    alt={photo.filename || 'Фото маршрута'}
                    onError={(e) => {
                      e.target.src = '/placeholder-image.jpg';
                    }}
                  />
                  <button
                    type="button"
                    onClick={() => setAsMainPhoto(photo.id)}
                    className="photo-btn-set-main"
                    title="Сделать основной"
                    disabled={submitting}
                  >
                    ★
                  </button>
                  <button 
                    type="button"
                    onClick={() => removeExistingPhoto(photo.id)}
                    className="photo-remove"
                    disabled={submitting}
                    title="Удалить фото"
                  >
                    ✕
                  </button>
                </div>
              ))}
            </div>
          )}
          
          {existingPhotos.length === 0 && (
            <p className="empty-message">Нет загруженных фотографий</p>
          )}
          
          <div className="admin-route-form_photo-upload">
            <label className={`btn btn-secondary ${submitting ? 'disabled' : ''}`}>
              📎 Добавить фотографии
              <input
                type="file"
                accept="image/*"
                multiple
                onChange={handlePhotoSelect}
                style={{ display: 'none' }}
                disabled={submitting}
              />
            </label>
            <span className="hint">Можно выбрать несколько файлов одновременно</span>
          </div>

          {tempPhotos.length > 0 && (
            <div className="photo-grid">
              {tempPhotos.map((photo, index) => (
                <div key={index} className="photo-item">
                  <img 
                    src={URL.createObjectURL(photo)} 
                    alt={photo.name}
                  />
                  <button 
                    type="button"
                    onClick={() => removeTempPhoto(index)}
                    className="photo-remove"
                    disabled={submitting}
                  >
                    ✕
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* ===== СЕКЦИЯ 5: ДОПОЛНИТЕЛЬНАЯ ИНФОРМАЦИЯ ===== */}
        <div className="admin-route-form_section">
          <div className="admin-route-form_section-header">
            <h3 className="admin-route-form_section-title">Дополнительная информация</h3>
            <button 
              type="button"
              onClick={addAdditionalInfo}
              className="btn btn-secondary btn-small"
              disabled={submitting}
            >
              + Добавить блок
            </button>
          </div>

          {formData.additionalInfo.length === 0 && (
            <p className="empty-message">Нет блоков дополнительной информации</p>
          )}

          {formData.additionalInfo.map((info, index) => (
            <div key={info.id || index} className="info-block">
              <div className="info-block-header">
                <h4>Блок {index + 1}</h4>
                <button 
                  type="button"
                  onClick={() => removeAdditionalInfo(index)}
                  className="btn-icon btn-remove"
                  disabled={submitting}
                >
                  ✕
                </button>
              </div>
              
              <div className="admin-route-form_field">
                <label>Заголовок</label>
                <input
                  type="text"
                  value={info.title}
                  onChange={(e) => handleAdditionalInfoChange(index, 'title', e.target.value)}
                  disabled={submitting}
                  placeholder="Введите заголовок"
                />
              </div>

              <div className="admin-route-form_field">
                <label>Описание</label>
                <textarea
                  value={info.description}
                  onChange={(e) => handleAdditionalInfoChange(index, 'description', e.target.value)}
                  disabled={submitting}
                  placeholder="Введите описание"
                  rows="3"
                />
              </div>
            </div>
          ))}
        </div>

        {/* ===== СЕКЦИЯ 6: ТОЧКИ МАРШРУТА (без карты) ===== */}
        <div className="admin-route-form_section">
          <div className="admin-route-form_section-header">
            <h3 className="admin-route-form_section-title">Точки маршрута</h3>
            <div className="section-buttons">
              <button 
                type="button"
                onClick={addPoint}
                className="btn btn-secondary btn-small"
                disabled={submitting}
              >
                + Добавить точку
              </button>
              {formData.points.length > 0 && (
                <button 
                  type="button"
                  onClick={clearAllPoints}
                  className="btn btn-secondary btn-small"
                  disabled={submitting}
                >
                  Очистить все точки
                </button>
              )}
            </div>
          </div>

          {formData.points.length === 0 && (
            <p className="empty-message">Нет добавленных точек</p>
          )}

          {formData.points.map((point, pointIndex) => {
            const pointPhotoList = point.id ? pointPhotos[point.id] || point.photos || [] : point.photos || [];
            return (
              <div key={point.id || pointIndex} className="point-block">
                <div className="point-block-header">
                  <h5>Точка {pointIndex + 1}</h5>
                  <div className="point-block-actions">
                    {pointIndex > 0 && (
                      <button
                        type="button"
                        onClick={() => reorderPoints(pointIndex, pointIndex - 1)}
                        className="btn-icon btn-move-up"
                        disabled={submitting}
                        title="Переместить вверх"
                      >
                        ↑
                      </button>
                    )}
                    {pointIndex < formData.points.length - 1 && (
                      <button
                        type="button"
                        onClick={() => reorderPoints(pointIndex, pointIndex + 1)}
                        className="btn-icon btn-move-down"
                        disabled={submitting}
                        title="Переместить вниз"
                      >
                        ↓
                      </button>
                    )}
                    {formData.points.length > 1 && (
                      <button 
                        type="button"
                        onClick={() => removePoint(pointIndex)}
                        className="btn-icon btn-remove"
                        disabled={submitting}
                        title="Удалить точку"
                      >
                        ✕
                      </button>
                    )}
                  </div>
                </div>

                <div className="admin-route-form_field">
                  <label>Название точки</label>
                  <input
                    type="text"
                    value={point.name}
                    onChange={(e) => updatePointInfo(pointIndex, 'name', e.target.value)}
                    disabled={submitting}
                    placeholder="Введите название точки"
                  />
                </div>

                <div className="admin-route-form_field">
                  <label>Описание точки</label>
                  <textarea
                    value={point.description}
                    onChange={(e) => updatePointInfo(pointIndex, 'description', e.target.value)}
                    disabled={submitting}
                    placeholder="Введите описание точки"
                    rows="2"
                  />
                </div>

                {/* Поле для координат */}
                <div className="admin-route-form_field">
                  <label>Координаты</label>
                  <input
                    type="text"
                    value={point.coordinates}
                    onChange={(e) => updatePointInfo(pointIndex, 'coordinates', e.target.value)}
                    disabled={submitting}
                    placeholder="55.751244, 37.618423"
                    className={coordinateErrors[pointIndex] ? 'error-input' : ''}
                  />
                  <p className="hint">Формат: широта, долгота (через запятую и пробел)</p>
                  {coordinateErrors[pointIndex] && (
                    <div className="error-message">{coordinateErrors[pointIndex]}</div>
                  )}
                </div>

                <div className="point-photos">
                  <label>Фотографии точки</label>
                  
                  {pointPhotoList.length > 0 && (
                    <div className="photo-grid">
                      {pointPhotoList.map((photo) => (
                        <div key={photo.id} className="photo-item">
                          <img 
                            src={photo.url} 
                            alt={photo.filename}
                            onError={(e) => {
                              e.target.src = '/placeholder-image.jpg';
                            }}
                          />
                          <button
                            type="button"
                            onClick={() => removePointPhoto(pointIndex, photo.id)}
                            className="photo-remove"
                            disabled={submitting}
                          >
                            ✕
                          </button>
                        </div>
                      ))}
                    </div>
                  )}

                  <div className="photo-upload">
                    <label className={`btn btn-secondary btn-small ${submitting ? 'disabled' : ''}`}>
                      📎 Добавить фото
                      <input
                        type="file"
                        accept="image/*"
                        multiple
                        onChange={(e) => handlePointPhotoSelect(pointIndex, e.target.files)}
                        style={{ display: 'none' }}
                        disabled={submitting}
                      />
                    </label>
                  </div>

                  {tempPointPhotos[pointIndex] && tempPointPhotos[pointIndex].length > 0 && (
                    <div className="photo-grid">
                      {tempPointPhotos[pointIndex].map((photo, photoIndex) => (
                        <div key={photoIndex} className="photo-item">
                          <img 
                            src={URL.createObjectURL(photo)} 
                            alt={photo.name}
                          />
                          <button
                            type="button"
                            onClick={() => removePointTempPhoto(pointIndex, photoIndex)}
                            className="photo-remove"
                            disabled={submitting}
                          >
                            ✕
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* ===== КНОПКИ ДЕЙСТВИЙ ===== */}
        <div className="admin-route-form_actions">
          <button 
            type="submit" 
            className="btn btn-primary"
            disabled={submitting || !formData.name.trim()}
          >
            {submitting ? (
              <>
                <span className="btn-spinner"></span>
                Сохранение...
              </>
            ) : (
              'Сохранить изменения'
            )}
          </button>
          <button 
            type="button" 
            onClick={() => navigate('/admin/locations/routes')} 
            className="btn btn-secondary"
            disabled={submitting}
          >
            Отмена
          </button>
        </div>
      </form>
    </div>
  );
};

export default AdminRouteEdit;